/**
 * Saved Inkings — Inking-mode sidebar listing persisted raster snapshots.
 *
 * Each entry bundles a full-resolution raster (stored as a PNG data URL in
 * `SavedInkingView`) with the camera state active at save time, plus an
 * auto-created camera bookmark so the view also appears as a frustum gizmo
 * elsewhere. Tapping a row animates the camera back and reloads the raster.
 */

import { useCallback, useState } from "react";
import { type SavedInkingView, useStore } from "../../app/store.ts";
import { Button, IconButton } from "../Button.tsx";
import { showConfirmDialog } from "../ConfirmDialog.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_BORDER,
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_PANEL_TITLE,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_SM,
} from "../tokens.ts";

export function SavedInkingsPanelBody() {
  const views = useStore((s) => s.savedInkingViews);
  const hasRaster = useStore(
    (s) => (s.inkingMode?.rasterBuffer ?? null) !== null,
  );
  const save = useStore((s) => s.saveCurrentInkingView);
  const remove = useStore((s) => s.deleteSavedInkingView);
  const rename = useStore((s) => s.renameSavedInkingView);
  const load = useStore((s) => s.loadSavedInkingView);

  const handleSave = useCallback(() => {
    save();
  }, [save]);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_PANEL_TITLE,
            fontWeight: 500,
            color: COLOR_TEXT_MUTED,
          }}
        >
          Saved Inkings
        </span>
        <Button
          onClick={handleSave}
          disabled={!hasRaster}
          title={
            hasRaster
              ? "Save the current inking and camera view"
              : "Generate inking first to enable saving"
          }
          variant="secondary"
          size="xs"
          style={{
            background: COLOR_ACTIVE_BG_SUBTLE,
            color: hasRaster ? COLOR_ACCENT : COLOR_TEXT_DISABLED,
          }}
        >
          + Save
        </Button>
      </div>

      {views.length === 0 && (
        <div
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_DISABLED,
            padding: "4px 0",
          }}
        >
          No saved inkings yet. Generate the inking, then tap Save to remember
          this view.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {views.map((v) => (
          <SavedInkingRow
            key={v.id}
            view={v}
            onLoad={() => load(v.id)}
            onRemove={async () => {
              const ok = await showConfirmDialog(
                `Delete "${v.name}"? This also removes its linked camera view.`,
              );
              if (ok) remove(v.id);
            }}
            onRename={(name) => rename(v.id, name)}
          />
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// SavedInkingRow
// ---------------------------------------------------------------------------

function SavedInkingRow({
  view,
  onLoad,
  onRemove,
  onRename,
}: {
  view: SavedInkingView;
  onLoad: () => void;
  onRemove: () => void;
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(view.name);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: 6,
        borderRadius: RADIUS_CONTROL,
        border: `0.5px solid ${COLOR_BORDER}`,
        background: COLOR_SURFACE,
      }}
    >
      <button
        onClick={onLoad}
        title={`Load "${view.name}"`}
        style={{
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        <img
          src={view.thumbnail}
          alt=""
          draggable={false}
          style={{
            width: 56,
            height: 56,
            objectFit: "cover",
            borderRadius: RADIUS_SM,
            border: `0.5px solid ${COLOR_BORDER}`,
            background: "#ffffff",
            display: "block",
          }}
        />
      </button>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {editing ? (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              onRename(draft);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onRename(draft);
                setEditing(false);
              } else if (e.key === "Escape") {
                setDraft(view.name);
                setEditing(false);
              }
            }}
            style={{
              fontSize: FONT_SIZE_BODY,
              color: COLOR_TEXT_SECONDARY,
              background: "transparent",
              border: `0.5px solid ${COLOR_BORDER_STRONG}`,
              borderRadius: RADIUS_SM,
              padding: "2px 4px",
              outline: "none",
            }}
          />
        ) : (
          <button
            onClick={onLoad}
            onDoubleClick={() => {
              setDraft(view.name);
              setEditing(true);
            }}
            title={`Load "${view.name}" — double-click to rename`}
            style={{
              textAlign: "left",
              padding: 0,
              border: "none",
              background: "transparent",
              color: COLOR_TEXT_SECONDARY,
              fontSize: FONT_SIZE_BODY,
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {view.name}
          </button>
        )}
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_FAINT,
          }}
        >
          {view.rasterWidth}×{view.rasterHeight} · {view.focalLength}mm
        </span>
      </div>

      <IconButton
        onClick={onRemove}
        title="Delete saved inking"
        icon={<span aria-hidden>×</span>}
        label="Delete saved inking"
        variant="secondary"
        size="xs"
        style={{
          background: "transparent",
          color: COLOR_TEXT_FAINT,
          flexShrink: 0,
        }}
      />
    </div>
  );
}
